import { AddRounded, BusinessRounded, CancelRounded, CheckCircleRounded, DescriptionRounded, DownloadRounded, PersonRounded } from "@mui/icons-material";
import { Box, Chip, IconButton, useTheme } from "@mui/material";
import { ColumnMovedEvent, GridReadyEvent } from "ag-grid-community";
import { ColDef, ICellRendererParams, IServerSideDatasource } from "ag-grid-enterprise";
import { useCallback, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";
import AgGridCustomTable from "../../../util/component/AgGridCustomTable";
import { GlobalContext } from "../../../util/state/GlobalContextProvider";
import { getCustomerBaseByIdAsync, searchCustomerByFiltersAsync } from "../api/service/customerbaseApi";
import { customerSearchFiltersState } from "../state/customerbaseState";
import { CBPartnerType, CustomerBaseDto } from "../types";
import { getCustomChipColor } from "../utils";

interface CustomerBaseTableProps {
    activePartnerType: "all" | "person" | "organization",
    onCustomerSelected?: (selectedCustomer: CustomerBaseDto) => void
}

const CustomerBaseTable = ({
    activePartnerType,
    onCustomerSelected
}: CustomerBaseTableProps) => {

    //const theme = useTheme();
    const {t} = useTranslation('customerbase');
    const { globalData } = useContext(GlobalContext);
    const customerSearchFilters = useRecoilValue(customerSearchFiltersState);

    const columnStateStorageKey = useMemo(() => {
        return `__CUSTOMERBASE_SEARCH.tableColumsConfig:${globalData.user.username}`

    }, [globalData.user.username]);

    const columnDefs = useMemo<ColDef[]>(() => {

        var colDef: ColDef[] = []
        if(activePartnerType == "all") {
            colDef = [
                {
                    colId: 'partnerType',
                    field: 'partnerType', 
                    headerName: t('partnerType'),
                    minWidth: 120,
                    resizable: false,
                    sortable: false,
                    cellStyle: {
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: "center"
                    },
                    cellRenderer: (params: ICellRendererParams) => {
                        return (
                            <Box display="flex" justifyContent="center" alignItems="center">
                                {params.data && params.data.partnerType && params.data.partnerType == CBPartnerType.PERSON &&
                                    <PersonRounded titleAccess={t("sapEnum.partnerTypes.Person")}/>
                                }
                                {params.data && params.data.partnerType && params.data.partnerType == CBPartnerType.ORGANIZATION &&
                                    <BusinessRounded titleAccess={t("sapEnum.partnerTypes.Organization")}/>
                                }
                            </Box>
                        );
                    }
                },
                {colId: 'CBCUSTOMERID', field: 'cbCustomerId', headerName: t('cbCustomerId')},
                {colId: 'BPCODE', field: 'bpCode', headerName: t('bpCode')},
                {colId: 'SURNAME', field: 'registry.lastName', headerName: t('lastName')},
                {colId: 'NAME', field: 'registry.firstName', headerName: t('firstName')},
                {colId: 'BUSINESSNAME', field: 'registry.businessName', headerName: t('businessName')},
                {colId: 'TAXCODE', field: 'registry.taxCode', headerName: t('taxCode')},
                {
                    colId: 'VATNUMBER',
                    field: 'registry.vatNumber', 
                    headerName: t('vatNumber'),
                    cellRenderer: (params: ICellRendererParams) => {
                        return !!params?.data?.registry?.vatNumberCountry && !!params?.data?.registry?.vatNumber ? params?.data?.registry?.vatNumberCountry + params?.data?.registry?.vatNumber : params?.data?.registry?.vatNumber || '';
                    }
                },
                {colId: 'DRIVERLICENCE', field: 'registry.drvLicenceNumber', headerName: t('drvLicenceNumber')},
                {colId: 'EMAIL', field: 'email', headerName: t('email')},
                {colId: 'DOSSIERORIGIN', field: 'dossiers.0.originIdentifier', headerName: t('dossierOrigin'), sortable: false},
                /*{
                    colId: 'DOSSIER_STATUS',
                    headerName: t('dossierStatus'),
                    sortable: false,
                    cellStyle: {
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: "center"
                    },
                    cellRenderer: (params: ICellRendererParams) => {
                        const dossier = params.data?.dossiers?.at(0) || null;

                        return (
                            <Box display="flex" justifyContent="center" alignItems="center">
                                {!!dossier &&
                                    <Chip 
                                        label={t(`documentStatus.${dossier.status}`)}
                                        sx={getCustomChipColor(dossier.status, theme)}
                                    />
                                }
                            </Box>
                        );
                    }
                },
                {
                    colId: 'DIGITAL_IDENTITY',
                    field: 'idpUid', 
                    headerName: t('idpUid'),
                    sortable: false,
                    cellStyle: {
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: "center"
                    },
                    cellRenderer: (params: ICellRendererParams) => {
                        return (
                            <Box display="flex" justifyContent="center" alignItems="center">
                                {!!params.data?.idpUid &&
                                    <CheckCircleRounded sx={{color: theme.palette.success.main}}/>
                                }
                                {!params.data?.idpUid &&
                                    <CancelRounded sx={{color: theme.palette.error.main}}/>
                                }
                            </Box>
                        );
                    }
                },
                {
                    colId: 'DIGITAL_IDENTITY_STATUS',
                    field: 'digitalIdentityStatus', 
                    headerName: t('digitalIdentityStatus'),
                    sortable: false,
                    cellStyle: {
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: "center"
                    },
                    cellRenderer: (params: ICellRendererParams) => {
                        const c = getCustomChipColor(params.data.digitalIdentityStatus, theme);

                        return (
                            <Box display="flex" justifyContent="center" alignItems="center">
                                {!!params.data?.digitalIdentityStatus &&
                                    <Chip 
                                        label={t(`sapEnum.digitalIdentityStatus.${params.data.digitalIdentityStatus}`)}
                                        sx={{color: c.color, backgroundColor: c.backgroundColor}}
                                    />
                                }
                            </Box>
                        );
                    }
                },*/
                {
                    field: '_actions',
                    headerName: '',
                    minWidth: onCustomerSelected ? 100 : 80,
                    maxWidth: onCustomerSelected ? 100 : 80,
                    sortable: false,
                    suppressSizeToFit: true,
                    pinned: 'right',
                    resizable: false,
                    suppressMovable: true,
                    suppressColumnsToolPanel: true,
                    lockPosition: "right", 
                    lockVisible: true, 
                    lockPinned: true,
                    cellRenderer: (params: ICellRendererParams) => {
                        return (
                            <Box display="flex" justifyContent="center" alignItems="center" gap={1}>
                                <IconButton 
                                    title="Details" 
                                    color="accent" 
                                    onClick={() => window.open(`/customerbase/view/${params.data?.cbCustomerId}`, "_blank")}
                                >
                                    <DescriptionRounded titleAccess={t("labels.actionDetails")}/>
                                </IconButton>
                                {!!onCustomerSelected &&
                                    <IconButton
                                        title="Select" 
                                        color="accent"
                                        onClick={() => onCustomerSelected(params.data)}
                                    >
                                        <AddRounded titleAccess={t("labels.actionSelect")}/>
                                    </IconButton>
                                }
                            </Box>
                        )
                    }
                }
            ];
        }

        if(activePartnerType == "organization") {
            colDef = [
                {
                    colId: 'partnerType',
                    field: 'partnerType', 
                    headerName: t('partnerType'),
                    minWidth: 120,
                    resizable: false,
                    sortable: false,
                    cellRenderer: (params: ICellRendererParams) => {
                        return (
                            <Box>
                                {params.data && params.data.partnerType && params.data.partnerType == CBPartnerType.PERSON &&
                                    <PersonRounded titleAccess={t("sapEnum.partnerTypes.Person")}/>
                                }
                                {params.data && params.data.partnerType && params.data.partnerType == CBPartnerType.ORGANIZATION &&
                                    <BusinessRounded titleAccess={t("sapEnum.partnerTypes.Organization")}/>
                                }
                            </Box>
                        );
                    }
                },
                {colId: 'CBCUSTOMERID', field: 'cbCustomerId', headerName: t('cbCustomerId'), minWidth: 140},
                {colId: 'BPCODE', field: 'bpCode', headerName: t('bpCode'), minWidth: 140},
                {colId: 'BUSINESSNAME', field: 'registry.businessName', headerName: t('businessName'), minWidth: 240},
                {colId: 'TAXCODE', field: 'registry.taxCode', headerName: t('taxCode'), minWidth: 200},
                {
                    colId: 'VATNUMBER',
                    field: 'registry.vatNumber', 
                    headerName: t('vatNumber'),
                    minWidth: 200,
                    cellRenderer: (params: ICellRendererParams) => {
                        return !!params?.data?.registry?.vatNumberCountry && !!params?.data?.registry?.vatNumber ? params?.data?.registry?.vatNumberCountry + params?.data?.registry?.vatNumber : params?.data?.registry?.vatNumber || '';
                    }
                },
                {colId: 'EMAIL', field: 'email', headerName: t('email'), minWidth: 240},
                {
                    field: '_actions',
                    headerName: '',
                    minWidth: onCustomerSelected ? 100 : 80,
                    maxWidth: onCustomerSelected ? 100 : 80,
                    sortable: false,
                    suppressSizeToFit: true,
                    pinned: 'right',
                    resizable: false,
                    suppressMovable: true,
                    suppressColumnsToolPanel: true,
                    lockPosition: "right", 
                    lockVisible: true, 
                    lockPinned: true,
                    cellRenderer: (params: ICellRendererParams) => {
                        return (
                            <Box display="flex" justifyContent="center" alignItems="center" gap={1}>
                                <IconButton 
                                    title="Details" 
                                    color="accent" 
                                    onClick={() => window.open(`/customerbase/view/${params.data?.cbCustomerId}`, "_blank")}
                                >
                                    <DescriptionRounded titleAccess={t("labels.actionDetails")}/>
                                </IconButton>
                                {!!onCustomerSelected &&
                                    <IconButton
                                        title="Select" 
                                        color="accent"
                                        onClick={() => onCustomerSelected(params.data)}
                                    >
                                        <AddRounded titleAccess={t("labels.actionSelect")}/>
                                    </IconButton>
                                }
                            </Box>
                        )
                    }
                }
            ];
        }

        return colDef;
        
    }, [t, onCustomerSelected, activePartnerType])

    const defaultColDef = useMemo(() => {
        return {
            minWidth: 180,
            sortable: true,
            unSortIcon: true          
        }
    }, []);

    const exportData = () => {
        //TODO
    };

    const serverSideDatasource = useMemo<IServerSideDatasource>(() => ({
        getRows: async (params) => {
            try {
                var data: any[] = [];
                var total: number = 0;
                if(customerSearchFilters){
                    if(customerSearchFilters.cbCustomerId) {
                        const res = (await getCustomerBaseByIdAsync(customerSearchFilters?.cbCustomerId)).data;
                        data = res !== undefined ? [res] : [];
                        total = data.length;
                    }
                    else {
                        const start = params.request.startRow;
                        const limit = (params.request.endRow !== undefined && params.request.startRow !== undefined) ? (params.request.endRow - params.request.startRow) : undefined;
                        const resp = (await searchCustomerByFiltersAsync({
                            filters: customerSearchFilters,
                            orderBy: params.request.sortModel[0] ? params.request.sortModel[0].colId : null,
                            orderDir: params.request.sortModel[0] ? params.request.sortModel[0].sort.toUpperCase() : null,
                            start: start,
                            limit: limit
                        }));
                        data = resp.data;
                        total = (isNaN(Number(resp.customFields.size)) ? data.length : Number(resp.customFields.size));
                    }
                }
                
                
                params.success({rowCount: total, rowData: data });
                total == 0 && params.api.showNoRowsOverlay();
            }
            catch {
                params.fail();
                params.api.hideOverlay();
            }
        }
    }), [customerSearchFilters])

    const onGridReady = useCallback((event: GridReadyEvent) => {
        const colState = JSON.parse(localStorage.getItem(columnStateStorageKey) || '[]');
        event.columnApi.applyColumnState({
            state: colState,
            applyOrder: true
        });
    }, [columnStateStorageKey]);

    const onColumnMoved = useCallback((event: ColumnMovedEvent) => {
        localStorage.setItem(columnStateStorageKey, JSON.stringify(event.columnApi.getColumnState()));
    }, [columnStateStorageKey]);

    return (
        <Box>
            {/*Da rivedere la logica di disabled
                <Card>
                    <Box pr={1} display="flex" justifyContent="flex-end" alignItems="center">
                        <IconButton color="accent" disabled={totalData === 0} onClick={()=>exportData()}>
                            <DownloadRounded/>
                        </IconButton>
                    </Box>
                </Card>*/
            }
            <AgGridCustomTable
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                onGridReady={onGridReady}
                onColumnMoved={onColumnMoved}
                rowModelType="serverSide"
                serverSideDatasource={serverSideDatasource}
                //paginationPageSize={10}
            />
        </Box>
    )
}

export default CustomerBaseTable;